import { useState, useMemo } from 'react';
import { useFormacao } from '@/context/FormacaoContext';
import { estadoNormalizado } from '@/utils/estadoUtils';

interface EntidadeStats {
  nome: string;
  tipo: string;
  acoes: number;
  horasFormandos: number;
  participantes: number;
  certificados: number;
  taxaCert: number;
  ultimaAcao: string | null;
  estados: Record<string, number>;
  categorias: string[];
  formacoes: string[];
  formacaoIds: string[];
}

const TIPO_CFG: Record<string, { label: string; bg: string; text: string; icon: string }> = {
  publica:      { label: 'Pública',         bg: 'bg-sky-50',     text: 'text-sky-700',     icon: 'ri-government-line'   },
  associacao:   { label: 'Associação',       bg: 'bg-emerald-50', text: 'text-emerald-700', icon: 'ri-team-line'         },
  solidaria:    { label: 'Solidariedade',    bg: 'bg-rose-50',    text: 'text-rose-700',    icon: 'ri-heart-line'        },
  privada:      { label: 'Privada',          bg: 'bg-amber-50',   text: 'text-amber-700',   icon: 'ri-building-2-line'   },
};

function classificarTipo(nome: string): string {
  const n = nome.toLowerCase();
  if (n.includes(' ina') || n.startsWith('ina') || n.includes('dgaep') || n.includes('cnpd') || n.includes('ersar') || n.includes('direção-geral') || n.includes('entidade reguladora')) return 'publica';
  if (n.includes('associação') || n.includes('associacao') || n.includes('atam') || n.includes('sindicato') || n.includes('ordem')) return 'associacao';
  if (n.includes('cruz vermelha') || n.includes('misericórdia') || n.includes('santa casa')) return 'solidaria';
  return 'privada';
}

function getInitiais(nome: string): string {
  const partes = nome.replace(/[—\-–]/g, ' ').split(/\s+/).filter(Boolean);
  if (partes.length >= 2) return (partes[0][0] + partes[1][0]).toUpperCase();
  return nome.substring(0, 2).toUpperCase();
}

export default function EntidadesFormacaoPage() {
  const { formacoes, participantes, loading } = useFormacao();
  const [search, setSearch] = useState('');
  const [tipoFilter, setTipoFilter] = useState('');
  const [sortBy, setSortBy] = useState<'acoes' | 'participantes' | 'taxaCert'>('acoes');
  const [expandida, setExpandida] = useState<string | null>(null);

  // ── Calcular estatísticas reais ──────────────────────────────────────────────
  const entidades = useMemo<EntidadeStats[]>(() => {
    const mapa: Record<string, EntidadeStats> = {};

    formacoes.forEach((f) => {
      const nome = f.entidade_formadora?.trim() || '(Sem entidade)';
      if (!mapa[nome]) {
        mapa[nome] = {
          nome,
          tipo: classificarTipo(nome),
          acoes: 0,
          horasFormandos: 0,
          participantes: 0,
          certificados: 0,
          taxaCert: 0,
          ultimaAcao: null,
          estados: {},
          categorias: [],
          formacoes: [],
          formacaoIds: [],
        };
      }
      const e = mapa[nome];
      e.acoes += 1;
      e.estados[f.estado] = (e.estados[f.estado] ?? 0) + 1;
      if (!e.categorias.includes(f.categoria ?? '')) e.categorias.push(f.categoria ?? '');
      e.formacoes.push(f.nome);
      e.formacaoIds.push(f.id);
      if (f.data_fim && (!e.ultimaAcao || f.data_fim > e.ultimaAcao)) e.ultimaAcao = f.data_fim;

      const parts = participantes.filter((p) => p.formacao_id === f.id);
      e.participantes += parts.length;
      e.certificados += parts.filter((p) => p.certificado_emitido).length;
      e.horasFormandos += Number(f.duracao_horas) * parts.length;
    });

    return Object.values(mapa).map((e) => ({
      ...e,
      taxaCert: e.participantes > 0 ? Math.round((e.certificados / e.participantes) * 100) : 0,
    })).sort((a, b) => b[sortBy] - a[sortBy]);
  }, [formacoes, participantes, sortBy]);

  const filtered = useMemo(() => entidades.filter((e) => {
    const matchSearch = search === '' || e.nome.toLowerCase().includes(search.toLowerCase());
    const matchTipo = tipoFilter === '' || e.tipo === tipoFilter;
    return matchSearch && matchTipo;
  }), [entidades, search, tipoFilter]);

  // ── Totais ────────────────────────────────────────────────────────────────────
  const totais = useMemo(() => ({
    entidades: entidades.length,
    acoes: entidades.reduce((s, e) => s + e.acoes, 0),
    participantes: entidades.reduce((s, e) => s + e.participantes, 0),
    certificados: entidades.reduce((s, e) => s + e.certificados, 0),
    taxaMedia: entidades.length > 0
      ? Math.round(entidades.filter((e) => e.participantes > 0).reduce((s, e) => s + e.taxaCert, 0) / Math.max(entidades.filter((e) => e.participantes > 0).length, 1))
      : 0,
  }), [entidades]);

  if (loading) {
    return (
      <main className="flex-1 bg-slate-50/60 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <i className="ri-loader-4-line text-3xl animate-spin text-zinc-300" />
          <p className="text-[13px] text-zinc-400">A carregar entidades...</p>
        </div>
      </main>
    );
  }

  return (
    <main className="flex-1 bg-slate-50/60 overflow-auto">
      {/* Header */}
      <div className="bg-white border-b border-zinc-100 px-8 py-5">
        <div className="flex items-center gap-4">
          <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-zinc-900 flex-shrink-0">
            <i className="ri-building-4-line text-white text-lg" />
          </div>
          <div>
            <span className="text-[11px] font-semibold text-zinc-400 uppercase tracking-widest">Formação</span>
            <h1 className="text-[20px] font-bold text-zinc-900 tracking-tight leading-tight">Entidades Formadoras</h1>
            <p className="text-[12px] text-zinc-400 mt-0.5">
              {totais.entidades} entidades parceiras · {totais.acoes} ações de formação
            </p>
          </div>
        </div>
      </div>

      <div className="px-8 py-6 space-y-5">
        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          {[
            { icon: 'ri-building-4-line',           value: totais.entidades,              label: 'Entidades',              bg: 'bg-zinc-100 text-zinc-600'      },
            { icon: 'ri-book-open-line',             value: totais.acoes,                  label: 'Ações Total',            bg: 'bg-zinc-100 text-zinc-600'      },
            { icon: 'ri-group-line',                 value: totais.participantes,          label: 'Participações',          bg: 'bg-amber-50 text-amber-600'     },
            { icon: 'ri-medal-line',                 value: totais.certificados,           label: 'Certificados',           bg: 'bg-emerald-50 text-emerald-600' },
            { icon: 'ri-percent-line',               value: `${totais.taxaMedia}%`,        label: 'Taxa Cert. Média',       bg: 'bg-zinc-100 text-zinc-600'      },
          ].map((s) => (
            <div key={s.label} className="bg-white rounded-xl border border-zinc-100 p-4 flex items-center gap-3">
              <div className={`w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0 ${s.bg}`}>
                <i className={`${s.icon} text-base`} />
              </div>
              <div>
                <p className="text-[20px] font-bold text-zinc-900 leading-tight">{s.value}</p>
                <p className="text-[11px] font-medium text-zinc-500">{s.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Destaques — cards grandes das 3 entidades principais */}
        {entidades.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {entidades.slice(0, 3).map((e, rank) => {
              const tc = TIPO_CFG[e.tipo] ?? TIPO_CFG['privada'];
              return (
                <div key={e.nome} className={`bg-white rounded-xl border overflow-hidden ${rank === 0 ? 'border-amber-200' : 'border-zinc-100'}`}>
                  {rank === 0 && (
                    <div className="bg-amber-400 px-4 py-1.5 flex items-center gap-2">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-trophy-line text-xs text-white" />
                      </div>
                      <span className="text-[11px] font-bold text-white uppercase tracking-wide">Principal Parceiro</span>
                    </div>
                  )}
                  <div className="p-5">
                    <div className="flex items-start gap-3 mb-4">
                      <div className={`w-11 h-11 flex items-center justify-center rounded-xl flex-shrink-0 ${rank === 0 ? 'bg-zinc-900' : 'bg-zinc-100'}`}>
                        <span className={`text-[12px] font-black ${rank === 0 ? 'text-white' : 'text-zinc-600'}`}>{getInitiais(e.nome)}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[13px] font-bold text-zinc-900 leading-snug">{e.nome}</p>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${tc.bg} ${tc.text}`}>{tc.label}</span>
                          {e.categorias.slice(0, 2).map((cat) => (
                            <span key={cat} className="text-[10px] text-zinc-400 bg-zinc-100 px-1.5 py-0.5 rounded-full">{cat}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3 mb-4">
                      {[
                        { label: 'Ações',    value: e.acoes },
                        { label: 'Part.',    value: e.participantes },
                        { label: 'Cert.',    value: e.certificados },
                      ].map((s) => (
                        <div key={s.label} className="bg-zinc-50 rounded-lg p-2.5 text-center">
                          <p className="text-[16px] font-black text-zinc-900">{s.value}</p>
                          <p className="text-[10px] text-zinc-400 font-medium">{s.label}</p>
                        </div>
                      ))}
                    </div>
                    {/* Taxa certificação */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[11px] text-zinc-400">Taxa de certificação</span>
                        <span className={`text-[12px] font-bold ${e.taxaCert === 100 ? 'text-emerald-600' : e.taxaCert >= 80 ? 'text-amber-600' : 'text-zinc-600'}`}>{e.taxaCert}%</span>
                      </div>
                      <div className="h-2 bg-zinc-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${e.taxaCert === 100 ? 'bg-emerald-500' : e.taxaCert >= 80 ? 'bg-amber-400' : 'bg-rose-400'}`}
                          style={{ width: `${e.taxaCert}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Filtros */}
        <div className="bg-white rounded-xl border border-zinc-100 p-4 flex flex-wrap gap-2.5 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <div className="w-8 h-8 flex items-center justify-center absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none">
              <i className="ri-search-line text-sm" />
            </div>
            <input
              type="text"
              placeholder="Pesquisar entidade..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-700 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-300"
            />
          </div>
          <select
            value={tipoFilter}
            onChange={(e) => setTipoFilter(e.target.value)}
            className="text-[13px] border border-zinc-200 rounded-lg px-3 py-2.5 bg-white text-zinc-600 focus:outline-none cursor-pointer"
          >
            <option value="">Todos os tipos</option>
            {Object.entries(TIPO_CFG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <div className="flex items-center gap-1 bg-zinc-100 rounded-lg p-1">
            {([
              { k: 'acoes',          l: 'Ações' },
              { k: 'participantes',  l: 'Part.' },
              { k: 'taxaCert',       l: 'Taxa Cert.' },
            ] as { k: typeof sortBy; l: string }[]).map(({ k, l }) => (
              <button
                key={k}
                onClick={() => setSortBy(k)}
                className={`whitespace-nowrap text-[12px] font-semibold px-2.5 py-1.5 rounded-md transition-all cursor-pointer ${
                  sortBy === k ? 'bg-white text-zinc-900' : 'text-zinc-400 hover:text-zinc-600'
                }`}
              >
                {l}
              </button>
            ))}
          </div>
          <span className="text-[12px] text-zinc-400 ml-auto">
            <span className="font-semibold text-zinc-600">{filtered.length}</span> entidade(s)
          </span>
        </div>

        {/* Lista expandível */}
        <div className="space-y-2">
          {filtered.map((ent, rank) => {
            const tc = TIPO_CFG[ent.tipo] ?? TIPO_CFG['privada'];
            const isOpen = expandida === ent.nome;
            const formacoesDetalhe = formacoes.filter((f) => f.entidade_formadora?.trim() === ent.nome);

            return (
              <div key={ent.nome} className="bg-white rounded-xl border border-zinc-100 overflow-hidden">
                <button
                  onClick={() => setExpandida(isOpen ? null : ent.nome)}
                  className="w-full text-left cursor-pointer"
                >
                  <div className="px-5 py-4 flex items-center gap-4">
                    {/* Rank */}
                    <span className={`w-7 h-7 flex items-center justify-center rounded-full text-[11px] font-black flex-shrink-0 ${
                      rank === 0 ? 'bg-amber-400 text-white' :
                      rank === 1 ? 'bg-zinc-300 text-zinc-700' :
                      rank === 2 ? 'bg-amber-700/70 text-white' :
                      'bg-zinc-100 text-zinc-500 text-[10px]'
                    }`}>{rank + 1}</span>

                    {/* Avatar */}
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-zinc-900 flex-shrink-0">
                      <span className="text-[9px] font-black text-white">{getInitiais(ent.nome)}</span>
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-[13px] font-bold text-zinc-900">{ent.nome}</p>
                        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${tc.bg} ${tc.text}`}>{tc.label}</span>
                      </div>
                      <p className="text-[11px] text-zinc-400 mt-0.5">
                        {ent.categorias.join(' · ')}
                        {ent.ultimaAcao && ` · Última ação: ${new Date(ent.ultimaAcao).toLocaleDateString('pt-PT', { month: 'long', year: 'numeric' })}`}
                      </p>
                    </div>

                    {/* Stats inline */}
                    <div className="hidden lg:flex items-center gap-6 flex-shrink-0">
                      {[
                        { label: 'Ações',    value: ent.acoes },
                        { label: 'Part.',    value: ent.participantes },
                        { label: 'Cert.',    value: ent.certificados },
                      ].map((s) => (
                        <div key={s.label} className="text-center">
                          <p className="text-[15px] font-bold text-zinc-900">{s.value}</p>
                          <p className="text-[10px] text-zinc-400">{s.label}</p>
                        </div>
                      ))}
                      <div className="text-center min-w-[60px]">
                        <p className={`text-[15px] font-bold ${ent.taxaCert === 100 ? 'text-emerald-600' : ent.taxaCert >= 80 ? 'text-amber-600' : ent.participantes === 0 ? 'text-zinc-300' : 'text-zinc-700'}`}>
                          {ent.participantes === 0 ? '—' : `${ent.taxaCert}%`}
                        </p>
                        <p className="text-[10px] text-zinc-400">Taxa Cert.</p>
                      </div>
                    </div>

                    {/* Expand icon */}
                    <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                      <i className={`ri-arrow-down-s-line text-zinc-400 text-lg transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
                    </div>
                  </div>
                </button>

                {/* Detalhe expandido */}
                {isOpen && (
                  <div className="border-t border-zinc-100 px-5 py-4 bg-zinc-50/50">
                    <p className="text-[11px] font-bold text-zinc-400 uppercase tracking-wide mb-3">Ações desta entidade</p>
                    <div className="space-y-2">
                      {formacoesDetalhe.map((f) => {
                        const parts = participantes.filter((p) => p.formacao_id === f.id);
                        const certs = parts.filter((p) => p.certificado_emitido).length;
                        const taxa = parts.length > 0 ? Math.round((certs / parts.length) * 100) : null;
                        return (
                          <div key={f.id} className="bg-white rounded-lg border border-zinc-100 px-4 py-3 flex items-center gap-4">
                            <div className="flex-1 min-w-0">
                              <p className="text-[13px] font-semibold text-zinc-800">{f.nome}</p>
                              <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                <span className="text-[10px] text-zinc-400">
                                  {f.data_inicio ? new Date(f.data_inicio).toLocaleDateString('pt-PT', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                                  {f.data_fim && f.data_fim !== f.data_inicio
                                    ? ` → ${new Date(f.data_fim).toLocaleDateString('pt-PT', { day: '2-digit', month: 'short', year: 'numeric' })}`
                                    : ''}
                                </span>
                                <span className="text-[10px] text-zinc-400">· {f.duracao_horas}h</span>
                                <span className="text-[10px] text-zinc-400">· {f.categoria}</span>
                              </div>
                            </div>
                            <div className="flex items-center gap-4 flex-shrink-0">
                              <div className="text-center">
                                <p className="text-[14px] font-bold text-zinc-900">{parts.length}</p>
                                <p className="text-[9px] text-zinc-400">part.</p>
                              </div>
                              {taxa !== null && (
                                <div className="text-center">
                                  <p className={`text-[13px] font-bold ${taxa === 100 ? 'text-emerald-600' : taxa >= 80 ? 'text-amber-600' : 'text-zinc-600'}`}>{taxa}%</p>
                                  <p className="text-[9px] text-zinc-400">cert.</p>
                                </div>
                              )}
                              <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${
                                estadoNormalizado(f.estado) === 'concluida' ? 'bg-emerald-50 text-emerald-700' : 'bg-zinc-100 text-zinc-600'
                              }`}>
                                {estadoNormalizado(f.estado) === 'concluida' ? 'Concluída' : 'Planeada'}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {filtered.length === 0 && (
            <div className="bg-white rounded-xl border border-zinc-100 py-16 flex flex-col items-center gap-3">
              <div className="w-12 h-12 flex items-center justify-center">
                <i className="ri-building-4-line text-4xl text-zinc-200" />
              </div>
              <p className="text-[13px] font-medium text-zinc-400">
                {entidades.length === 0 ? 'Nenhuma entidade formadora registada' : 'Nenhuma entidade encontrada'}
              </p>
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
